/**
 * JIRA/HubSpot Style Kanban Filter Bar
 * Quick filters and search functionality
 */
import React, { useState, useEffect } from 'react';
import { Search, Filter, X, ChevronDown, User, Building2, Flag, Globe, LayoutGrid, List } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const KanbanFilters = ({
  filters,
  onFilterChange,
  availableOEMs = [],
  availableRegions = [],
  availableAssignees = [],
  viewMode = 'kanban',
  onViewModeChange,
  swimlaneGrouping = null,
  onSwimlaneGroupingChange,
}) => {
  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      onFilterChange({ ...filters, search: searchTerm });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Count active filters
  const activeFilterCount = [
    filters.oem,
    filters.priority,
    filters.region,
    filters.assigned_to,
  ].filter(Boolean).length;

  // Handle filter clear
  const clearFilter = (filterKey) => {
    onFilterChange({ ...filters, [filterKey]: null });
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    onFilterChange({
      search: '',
      oem: null,
      priority: null,
      region: null,
      assigned_to: null,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Main Filter Bar */}
      <div className="flex items-center gap-3">
        {/* Search Input */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search opportunities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 bg-white border-slate-200 focus:border-blue-500 focus:ring-blue-500"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Quick Filters */}
        <div className="flex items-center gap-2">
          {/* OEM Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant={filters.oem ? "secondary" : "outline"} 
                size="sm" 
                className="h-9 gap-1.5"
              >
                <Building2 className="w-4 h-4" />
                <span className="hidden sm:inline">{filters.oem || 'OEM'}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>Filter by OEM</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {availableOEMs.map((oem) => (
                <DropdownMenuCheckboxItem
                  key={oem}
                  checked={filters.oem === oem}
                  onCheckedChange={(checked) => 
                    onFilterChange({ ...filters, oem: checked ? oem : null })
                  }
                >
                  {oem}
                </DropdownMenuCheckboxItem>
              ))}
              {filters.oem && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuCheckboxItem
                    checked={false}
                    onCheckedChange={() => clearFilter('oem')}
                    className="text-slate-500"
                  >
                    Clear filter
                  </DropdownMenuCheckboxItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Priority Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant={filters.priority ? "secondary" : "outline"} 
                size="sm" 
                className="h-9 gap-1.5"
              >
                <Flag className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {filters.priority ? filters.priority.charAt(0).toUpperCase() + filters.priority.slice(1) : 'Priority'}
                </span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Filter by Priority</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={filters.priority === 'high'}
                onCheckedChange={(checked) => 
                  onFilterChange({ ...filters, priority: checked ? 'high' : null })
                }
              >
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  High
                </span>
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={filters.priority === 'medium'}
                onCheckedChange={(checked) => 
                  onFilterChange({ ...filters, priority: checked ? 'medium' : null })
                }
              >
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                  Medium
                </span>
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={filters.priority === 'low'}
                onCheckedChange={(checked) => 
                  onFilterChange({ ...filters, priority: checked ? 'low' : null })
                }
              >
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-slate-400" />
                  Low
                </span>
              </DropdownMenuCheckboxItem>
              {filters.priority && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuCheckboxItem
                    checked={false}
                    onCheckedChange={() => clearFilter('priority')}
                    className="text-slate-500"
                  >
                    Clear filter
                  </DropdownMenuCheckboxItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Region Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant={filters.region ? "secondary" : "outline"} 
                size="sm" 
                className="h-9 gap-1.5"
              >
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{filters.region || 'Region'}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Filter by Region</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {availableRegions.map((region) => (
                <DropdownMenuCheckboxItem
                  key={region}
                  checked={filters.region === region}
                  onCheckedChange={(checked) => 
                    onFilterChange({ ...filters, region: checked ? region : null })
                  }
                >
                  {region}
                </DropdownMenuCheckboxItem>
              ))}
              {filters.region && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuCheckboxItem
                    checked={false}
                    onCheckedChange={() => clearFilter('region')}
                    className="text-slate-500"
                  >
                    Clear filter
                  </DropdownMenuCheckboxItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Assignee Filter */}
          {availableAssignees.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant={filters.assigned_to ? "secondary" : "outline"} 
                  size="sm" 
                  className="h-9 gap-1.5"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Assignee</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuLabel>Filter by Assignee</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {availableAssignees.map((assignee) => (
                  <DropdownMenuCheckboxItem
                    key={assignee.id}
                    checked={filters.assigned_to === assignee.id}
                    onCheckedChange={(checked) => 
                      onFilterChange({ ...filters, assigned_to: checked ? assignee.id : null })
                    }
                  >
                    {assignee.name}
                  </DropdownMenuCheckboxItem>
                ))}
                {filters.assigned_to && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                      checked={false}
                      onCheckedChange={() => clearFilter('assigned_to')}
                      className="text-slate-500"
                    >
                      Clear filter
                    </DropdownMenuCheckboxItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* More Filters Popover */}
          <Popover open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-1.5">
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">More</span>
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm">Filters</h4>
                  {activeFilterCount > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={clearAllFilters}
                      className="h-auto py-1 px-2 text-xs text-slate-500"
                    >
                      Clear all
                    </Button>
                  )}
                </div>
                
                {/* Swimlane Grouping */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-700">Group by (Swimlanes)</label>
                  <Select 
                    value={swimlaneGrouping || 'none'} 
                    onValueChange={(val) => onSwimlaneGroupingChange?.(val === 'none' ? null : val)}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (Flat view)</SelectItem>
                      <SelectItem value="oem">By OEM</SelectItem>
                      <SelectItem value="region">By Region</SelectItem>
                      <SelectItem value="assignee">By Assignee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center border border-slate-200 rounded-md overflow-hidden">
          <button
            onClick={() => onViewModeChange?.('kanban')}
            className={`p-2 transition-colors ${
              viewMode === 'kanban' 
                ? 'bg-slate-100 text-slate-900' 
                : 'bg-white text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
            title="Kanban view"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewModeChange?.('list')}
            className={`p-2 transition-colors ${
              viewMode === 'list' 
                ? 'bg-slate-100 text-slate-900' 
                : 'bg-white text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
            title="List view"
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Active Filter Badges */}
      {(activeFilterCount > 0 || searchTerm) && (
        <div className="flex items-center gap-2 flex-wrap">
          {searchTerm && (
            <Badge variant="secondary" className="gap-1 pr-1">
              Search: "{searchTerm}"
              <button 
                onClick={() => setSearchTerm('')}
                className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          {filters.oem && (
            <Badge variant="secondary" className="gap-1 pr-1">
              OEM: {filters.oem}
              <button 
                onClick={() => clearFilter('oem')}
                className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          {filters.priority && (
            <Badge variant="secondary" className="gap-1 pr-1">
              Priority: {filters.priority}
              <button 
                onClick={() => clearFilter('priority')}
                className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          {filters.region && (
            <Badge variant="secondary" className="gap-1 pr-1">
              Region: {filters.region}
              <button 
                onClick={() => clearFilter('region')}
                className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

export default KanbanFilters;
